import { useEffect, useMemo, useState } from 'react';

import { Field, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import DynamicPropertyMain from '../DynamicProperty/DynamicPropertyMain';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import projectService from '../../service/project.service';
import {
  FormErrorUI,
  FormLabelUI,
  InputUI,
  TabsUI,
  FileTabUI,
  DataGridIdentifiers,
  FileTabV2,
  DeleteDialogUI,
  CheckboxUI,
} from '../Interface';
import ProjectCommunication from './ProjectCommunication';
import ProjectContact from './ProjectContact';
import ProjectResourceOneTable from './Resources/ResourceOneTable';
import ProjectResourceTwoTable from './Resources/ResourceTwoTable';
import ProjectResourceThreeTable from './Resources/ResourceThreeTable';
import ProjectResourceFourTable from './Resources/ResourceFourTable';
import ProjectResourceFiveTable from './Resources/ResourceFiveTable';
import ProjectHistory from './ProjectHistory/ProjectHistory';
import CheckLists from '../CheckList/CheckListTab/CheckLists';
import ProjectTaskList from '../TaskList/TaskListTab/ProjectTaskList';
import useSaveView from '../../hooks/useSaveView';
import useView from '../../hooks/useView';
import ExcelImportTab from '../Interface/ExcelImportTab';
import jokerService from '../../service/joker.service';
import { JokerTypes } from '../../config/joker';
import JokerTab from '../Interface/Joker/JokerTab';
import ProjectPriceTable from './ProjectPriceTable';
import Comments from '../Comment/Comments';
import ProjectEmployeeTable from './ProjectEmployeeTable';
import useParam from '../../context/ParamContext';
import ProjectPaymentScheduleTable from './ProjectPaymentScheduleTable';
import ProjectSubcontractorsTable from './ProjectSubcontractorsTable';
import ProjectEmployeeAttendanceDetailsTable from './ProjectEmployeeAttendanceDetailsTable';
import ProjectExtraWorkSimpleTasksTable from './ProjectExtraWorkSimpleTasks/ProjectExtraWorkSimpleTasksTable';
import { FormType } from '../../config/form';
import ProjectSimpleTasksTable from './ProjectSimpleTasksTable';
import { folderTemplateItemsToFolders } from '../../utils/folderHelper';
import ProjectJokerLine from './ProjectJokerLine';

const PropertyGroups = (props) => {
  const { propertyGroups, groupState, user } = props;
  const { group, setGroup } = groupState;
  const saveView = useSaveView(user);
  const savedGroups = useView('projectPage', user);

  const handleCheckboxChange = (id) => {
    setGroup((prevGroup) => {
      const isSelected = prevGroup.id.includes(id);
      const updatedIds = isSelected ? prevGroup.id.filter((groupId) => groupId !== id) : [...prevGroup.id, id];

      const newGroup = { ...prevGroup, id: updatedIds };

      saveView('projectPage', { dynGroup: updatedIds });

      return newGroup;
    });
  };

  useEffect(() => {
    if (Object.keys(savedGroups).length > 0) {
      setGroup((prevGroup) => ({
        ...prevGroup,
        id: savedGroups.dynGroup,
      }));
    }
  }, [savedGroups]);

  return (
    <div className="flex -mt-5 flex-wrap">
      {propertyGroups.length > 0 &&
        propertyGroups.map((groupItem) => (
          <div key={groupItem.id}>
            <CheckboxUI
              label={groupItem.name}
              checked={group.id.includes(groupItem.id)}
              onChange={() => handleCheckboxChange(groupItem.id)}
            />
          </div>
        ))}
    </div>
  );
};

const DescriptionPanel = ({ disabled, errors, values }) => {
  return (
    <div>
      <div className="col-span-full relative">
        <div className="mt-2">
          <Field
            type="text"
            name="projectDescription"
            as={InputUI}
            className={`${disabled ? 'rounded-md' : ''} resize-none h-36, p-2`}
            fullWidth
            label={<FormLabelUI text="Leírás" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.projectDescription} />}
            disabled={disabled}
            InputLabelProps={{ shrink: values.projectDescription !== '' }}
            multiline
            rows={10}
          />
        </div>
      </div>
    </div>
  );
};

const ProjectTabs = (props) => {
  const { setFieldValue, values } = useFormikContext();
  const { params } = useParam();
  const { user, selectedValues, disabled, setRefetchTable, addField, dynLoadingState, dialog } = props;
  const { setDynLoading } = dynLoadingState;
  const projectId = selectedValues.projectId ?? values.projectId;
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialog, setDeletDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [jokerGroups, setJokerGroups] = useState([]);
  const [createdProjectFolders, setCreatedProjectFolders] = useState([]);
  const [updatedProjectFolders, setUpdatedProjectFolders] = useState([]);
  const [deletedProjectFolderIds, setDeletedProjectFolderIds] = useState([]);

  useEffect(() => {
    getJokerGroups();
  }, []);

  useEffect(() => {
    setCreatedProjectFolders([]);
    setUpdatedProjectFolders([]);
    setDeletedProjectFolderIds([]);
  }, [selectedValues.projectId]);

  const handleUpload = async (files, targetFolder) => {
    if (params.USE_FILETAB_V2 === 'Y' && projectId) {
      const folderId = targetFolder?.type === 'saved' ? targetFolder.id : null;
      const uploadedAttachments = await Promise.all(
        files.map((file) => projectService.uploadProjectAttachment(projectId, file, folderId))
      );
      const projectAttachments = [
        ...(values.projectAttachment ?? []).filter((attachment) => !(attachment instanceof File)),
        ...uploadedAttachments,
      ];

      await setFieldValue('projectAttachment', projectAttachments);
      addNotification({
        content: files.length > 1 ? 'A fájlok feltöltése sikeres!' : 'A fájl feltöltése sikeres!',
        type: NotificationType.SUCCESS,
      });
      return;
    }

    const filesWithId = files.map((file) => {
      file.id = uuidv4();
      return file;
    });

    const existingAttachments = Array.isArray(values.projectAttachment) ? values.projectAttachment : [];

    const fieldUpdates = [setFieldValue('projectAttachment', [...existingAttachments, ...filesWithId], false)];

    if (params.USE_FILETAB_V2 === 'Y' && targetFolder?.type !== 'root') {
      const firstFileIndex = existingAttachments.filter((file) => file instanceof File).length;
      const newAssignments = filesWithId.map((_, index) => ({
        fileIndex: firstFileIndex + index,
        ...(targetFolder.type === 'saved' ? { folderId: targetFolder.id } : { folderClientId: targetFolder.clientId }),
      }));

      fieldUpdates.push(
        setFieldValue('fileFolderAssignments', [...(values.fileFolderAssignments ?? []), ...newAssignments], false)
      );
    }

    await Promise.all(fieldUpdates);
  };

  const handleCreateFolder = (name, color, isProtected, parentFolder) => {
    if (addField && selectedValues.projectId) {
      return projectService
        .createProjectFolder(selectedValues.projectId, {
          name,
          color,
          protected: isProtected,
          parentId: parentFolder.type === 'saved' ? parentFolder.id : null,
        })
        .then((projectFolder) => {
          setCreatedProjectFolders((folders) => [...folders, projectFolder]);
          return projectFolder;
        });
    }

    const projectFolder = {
      clientId: uuidv4(),
      name,
      color,
      protected: isProtected,
      ...(parentFolder.type === 'saved'
        ? { parentId: parentFolder.id }
        : { parentClientId: parentFolder.type === 'new' ? parentFolder.clientId : null }),
    };

    setFieldValue('projectFolders', [...(values.projectFolders ?? []), projectFolder]);
  };

  const handleImportProjectFolder = (folderTemplateItems, parentFolder) => {
    const projectFolders = folderTemplateItemsToFolders(folderTemplateItems, parentFolder);

    if (addField && selectedValues.projectId) {
      return projectService
        .importProjectFolder(selectedValues.projectId, {
          folders: projectFolders,
        })
        .then((createdFolders) => {
          setCreatedProjectFolders((folders) => [...folders, ...createdFolders]);

          return createdFolders;
        });
    }

    setFieldValue('projectFolders', [...(values.projectFolders ?? []), ...projectFolders]);

    return projectFolders;
  };

  const handleMoveFile = async (file, targetFolder) => {
    if (!(file instanceof File)) {
      if (!selectedValues.projectId || !file.id || targetFolder.type === 'new') return;

      const folderId = targetFolder.type === 'saved' ? targetFolder.id : null;
      if ((file.folderId ?? null) === folderId) return;

      return projectService.moveProjectAttachment(selectedValues.projectId, file.id, { folderId }).then(() => {
        setFieldValue(
          'projectAttachment',
          (values.projectAttachment ?? []).map((projectAttachment) =>
            projectAttachment.id === file.id ? { ...projectAttachment, folderId } : projectAttachment
          )
        );

        addNotification({
          content: 'A fájl áthelyezése sikeres!',
          type: NotificationType.SUCCESS,
        });
      });
    }

    const fileIndex = (values.projectAttachment ?? [])
      .filter((projectAttachment) => projectAttachment instanceof File)
      .indexOf(file);
    if (fileIndex < 0) return;

    const remainingAssignments = (values.fileFolderAssignments ?? []).filter(
      (fileFolderAssignment) => fileFolderAssignment.fileIndex !== fileIndex
    );

    if (targetFolder.type === 'root') {
      await setFieldValue('fileFolderAssignments', remainingAssignments, false);
      return;
    }

    await setFieldValue(
      'fileFolderAssignments',
      [
        ...remainingAssignments,
        {
          fileIndex,
          ...(targetFolder.type === 'saved'
            ? { folderId: targetFolder.id }
            : { folderClientId: targetFolder.clientId }),
        },
      ],
      false
    );
  };

  const handleMoveFiles = async (files, targetFolder) => {
    const attachments = values.projectAttachment ?? [];
    const selectedFiles = files.filter((file) => attachments.includes(file));
    const savedFilesToMove = selectedFiles.filter(
      (file) =>
        !(file instanceof File) &&
        selectedValues.projectId &&
        file.id &&
        targetFolder.type !== 'new' &&
        (file.folderId ?? null) !== (targetFolder.type === 'saved' ? targetFolder.id : null)
    );
    const targetFolderId = targetFolder.type === 'saved' ? targetFolder.id : null;

    if (savedFilesToMove.length > 0) {
      await Promise.all(
        savedFilesToMove.map((file) =>
          projectService.moveProjectAttachment(selectedValues.projectId, file.id, { folderId: targetFolderId })
        )
      );

      const movedFileIds = new Set(savedFilesToMove.map((file) => file.id));
      setFieldValue(
        'projectAttachment',
        attachments.map((attachment) =>
          movedFileIds.has(attachment.id) ? { ...attachment, folderId: targetFolderId } : attachment
        )
      );
    }

    const newFiles = attachments.filter((attachment) => attachment instanceof File);
    const selectedNewFileIndexes = selectedFiles
      .filter((file) => file instanceof File)
      .map((file) => newFiles.indexOf(file))
      .filter((fileIndex) => fileIndex >= 0);

    if (selectedNewFileIndexes.length > 0) {
      const selectedNewFileIndexSet = new Set(selectedNewFileIndexes);
      const remainingAssignments = (values.fileFolderAssignments ?? []).filter(
        (assignment) => !selectedNewFileIndexSet.has(assignment.fileIndex)
      );
      const newAssignments =
        targetFolder.type === 'root'
          ? []
          : selectedNewFileIndexes.map((fileIndex) => ({
              fileIndex,
              ...(targetFolder.type === 'saved'
                ? { folderId: targetFolder.id }
                : { folderClientId: targetFolder.clientId }),
            }));

      await setFieldValue('fileFolderAssignments', [...remainingAssignments, ...newAssignments], false);
    }

    if (savedFilesToMove.length > 0 || selectedNewFileIndexes.length > 0) {
      addNotification({
        content: 'A fájlok áthelyezése sikeres!',
        type: NotificationType.SUCCESS,
      });
    }
  };

  const removeFiles = async (files) => {
    const attachments = values.projectAttachment ?? [];
    const filesToRemove = new Set(files);
    const removedFileIds = new Set(files.map((file) => file.id).filter((fileId) => fileId != null));
    const newFiles = attachments.filter((attachment) => attachment instanceof File);
    const removedNewFileIndexes = new Set(
      files
        .filter((file) => file instanceof File)
        .map((file) => newFiles.indexOf(file))
        .filter((fileIndex) => fileIndex >= 0)
    );

    await Promise.all([
      setFieldValue(
        'fileFolderAssignments',
        (values.fileFolderAssignments ?? [])
          .filter((assignment) => !removedNewFileIndexes.has(assignment.fileIndex))
          .map((assignment) => ({
            ...assignment,
            fileIndex:
              assignment.fileIndex -
              [...removedNewFileIndexes].filter((removedIndex) => removedIndex < assignment.fileIndex).length,
          })),
        false
      ),
      setFieldValue(
        'projectAttachment',
        attachments.filter(
          (attachment) =>
            !filesToRemove.has(attachment) && (attachment.id == null || !removedFileIds.has(attachment.id))
        ),
        false
      ),
    ]);
  };

  const handleDeleteFiles = async (files) => {
    const savedAttachmentIds = files
      .filter((file) => !(file instanceof File) && file.id != null)
      .map((file) => file.id);

    if (selectedValues.projectId && savedAttachmentIds.length === 1) {
      await projectService.deleteProjectAttachment(selectedValues.projectId, savedAttachmentIds[0]);
    } else if (selectedValues.projectId && savedAttachmentIds.length > 1) {
      await projectService.deleteProjectAttachments(selectedValues.projectId, savedAttachmentIds);
    }

    await removeFiles(files);
    addNotification({
      content: files.length > 1 ? 'A fájlok törlése sikeres!' : 'A fájl törlése sikeres!',
      type: NotificationType.SUCCESS,
    });
  };

  const handleRenameFile = async (file, newName) => {
    const attachments = values.projectAttachment ?? [];

    if (file instanceof File) {
      const renamedFile = new File([file], newName, { type: file.type, lastModified: file.lastModified });
      renamedFile.id = file.id;

      await setFieldValue(
        'projectAttachment',
        attachments.map((attachment) => (attachment === file ? renamedFile : attachment)),
        false
      );
      return;
    }

    const updated = await projectService.renameProjectAttachment(selectedValues.projectId, file.id, {
      name: newName,
    });

    setFieldValue(
      'projectAttachment',
      attachments.map((attachment) =>
        attachment.id === file.id ? { ...attachment, name: newName, ...(updated ?? {}) } : attachment
      )
    );

    addNotification({
      content: 'A fájl átnevezése sikeres!',
      type: NotificationType.SUCCESS,
    });
  };

  const handleEditFolder = async (folder, name, color, isProtected) => {
    if (folder.type === 'saved') {
      await projectService.updateProjectFolder(selectedValues.projectId, folder.id, {
        name,
        color,
        protected: isProtected,
      });
      setUpdatedProjectFolders((folders) => [
        ...folders.filter((item) => item.id !== folder.id),
        { ...folder, name, color, protected: isProtected },
      ]);
      return;
    }

    setFieldValue(
      'projectFolders',
      (values.projectFolders ?? []).map((item) =>
        item.clientId === folder.clientId ? { ...item, name, color, protected: isProtected } : item
      )
    );
  };

  const handleDeleteFolder = async (folder) => {
    if (folder.type === 'saved') {
      const savedFolders = [
        ...(selectedValues.projectFolder ?? []),
        ...createdProjectFolders,
        ...updatedProjectFolders,
      ];
      const folderIdsToDelete = new Set([folder.id]);
      let foundChildFolder = true;

      while (foundChildFolder) {
        foundChildFolder = false;
        savedFolders.forEach((item) => {
          if (folderIdsToDelete.has(item.parentId) && !folderIdsToDelete.has(item.id)) {
            folderIdsToDelete.add(item.id);
            foundChildFolder = true;
          }
        });
      }

      const assignedNewFileIndexes = new Set(
        (values.fileFolderAssignments ?? [])
          .filter((assignment) => folderIdsToDelete.has(assignment.folderId))
          .map((assignment) => assignment.fileIndex)
      );
      const newFiles = (values.projectAttachment ?? []).filter((attachment) => attachment instanceof File);
      const filesToDelete = [
        ...(values.projectAttachment ?? []).filter(
          (attachment) => !(attachment instanceof File) && folderIdsToDelete.has(attachment.folderId)
        ),
        ...newFiles.filter((_, fileIndex) => assignedNewFileIndexes.has(fileIndex)),
      ];
      const savedAttachmentIds = filesToDelete
        .filter((file) => !(file instanceof File) && file.id != null)
        .map((file) => file.id);

      if (savedAttachmentIds.length === 1) {
        await projectService.deleteProjectAttachment(selectedValues.projectId, savedAttachmentIds[0]);
      } else if (savedAttachmentIds.length > 1) {
        await projectService.deleteProjectAttachments(selectedValues.projectId, savedAttachmentIds);
      }

      await removeFiles(filesToDelete);
      const foldersById = new Map([...savedFolders, folder].map((item) => [item.id, item]));
      const folderIdsByDepth = [...folderIdsToDelete].sort((firstFolderId, secondFolderId) => {
        const getFolderDepth = (folderId) => {
          let depth = 0;
          let currentFolder = foldersById.get(folderId);

          while (currentFolder && folderIdsToDelete.has(currentFolder.parentId)) {
            depth += 1;
            currentFolder = foldersById.get(currentFolder.parentId);
          }

          return depth;
        };

        return getFolderDepth(secondFolderId) - getFolderDepth(firstFolderId);
      });

      for (const folderId of folderIdsByDepth) {
        await projectService.deleteProjectFolder(selectedValues.projectId, folderId);
      }

      setDeletedProjectFolderIds((folderIds) => [...new Set([...folderIds, ...folderIdsToDelete])]);
      setCreatedProjectFolders((folders) => folders.filter((item) => !folderIdsToDelete.has(item.id)));
      setUpdatedProjectFolders((folders) => folders.filter((item) => !folderIdsToDelete.has(item.id)));
      return;
    }

    const folders = values.projectFolders ?? [];
    const folderClientIdsToDelete = new Set([folder.clientId]);
    let foundChildFolder = true;

    while (foundChildFolder) {
      foundChildFolder = false;
      folders.forEach((item) => {
        if (folderClientIdsToDelete.has(item.parentClientId) && !folderClientIdsToDelete.has(item.clientId)) {
          folderClientIdsToDelete.add(item.clientId);
          foundChildFolder = true;
        }
      });
    }

    const assignedNewFileIndexes = new Set(
      (values.fileFolderAssignments ?? [])
        .filter((assignment) => folderClientIdsToDelete.has(assignment.folderClientId))
        .map((assignment) => assignment.fileIndex)
    );
    const newFiles = (values.projectAttachment ?? []).filter((attachment) => attachment instanceof File);
    await removeFiles(newFiles.filter((_, fileIndex) => assignedNewFileIndexes.has(fileIndex)));
    setFieldValue(
      'projectFolders',
      folders.filter((item) => !folderClientIdsToDelete.has(item.clientId))
    );
  };

  const fileTabDisabled = props.infoPanel && selectedValues.projectId ? false : disabled;

  const isOwnFile = (file) => file instanceof File || file.createdBy === user.userId;
  const canManageFile = (file) => Boolean(props.canEdit) || isOwnFile(file);

  const hideDeleteDialog = () => {
    setDeletDialog(false);
  };

  const handleRemoveElement = async () => {
    if (params.USE_FILETAB_V2 === 'Y') await handleDeleteFiles([fileToDelete]);
    else {
      setFieldValue(
        'projectAttachment',
        (values.projectAttachment || []).filter((projectAttachment) => projectAttachment.id !== fileToDelete.id)
      );
    }

    setFileToDelete(null);
    hideDeleteDialog();
  };

  const getJokerGroups = async () => {
    jokerService.getGroups(JokerTypes.PROJECT).then((data) => {
      setJokerGroups(data);
    });
  };

  const savedProjectFolders = useMemo(() => {
    const foldersById = new Map(
      [...(selectedValues.projectFolder ?? []), ...createdProjectFolders, ...updatedProjectFolders].map((folder) => [
        folder.id,
        folder,
      ])
    );

    return [...foldersById.values()].filter((folder) => !deletedProjectFolderIds.includes(folder.id));
  }, [createdProjectFolders, deletedProjectFolderIds, selectedValues.projectFolder, updatedProjectFolders]);

  const tabs = [
    {
      id: 0,
      name: 'Tulajdonságok',
      content: (
        <div>
          <PropertyGroups {...props} />
          <DynamicPropertyMain {...props} />
        </div>
      ),
      key: 'PROPERTIES',
    },
    {
      id: 1,
      name: 'Checklista',
      content: <CheckLists {...props} />,
      key: 'CHECKLIST',
    },
    {
      id: 2,
      name: 'Feladat lista',
      content: <ProjectTaskList {...props} />,
      key: 'TASKLIST',
    },
    {
      id: 3,
      name: 'Pótmunka',
      content: <ProjectTaskList {...props} isExtraWorkType={true} />,
      key: 'EXTRAWORK',
    },
    {
      id: 4,
      name: 'Megjegyzés',
      content: <DescriptionPanel {...props} />,
      key: 'DESCRIPTION',
    },
    {
      id: 5,
      name: 'Hozzászólások',
      content: (
        <Comments
          rows={selectedValues.comments ?? []}
          lineId={selectedValues.projectId}
          subMenuUrl="Project"
          setRefetchTable={dialog ? dialog?.setTaskChanged : setRefetchTable}
          disabled={disabled || !addField}
        />
      ),
      key: 'COMMENT',
    },
    {
      id: 6,
      name: 'Projekt tagok',
      content: <ProjectEmployeeTable {...props} />,
      key: 'PROJECT_EMPLOYEE',
    },
    {
      id: 7,
      name: props.submenuNames[0],
      content: <ProjectResourceOneTable {...props} />,
      key: 'RESOURCE_ONE',
    },
    {
      id: 8,
      name: props.submenuNames[1],
      content: <ProjectResourceTwoTable {...props} />,
      key: 'RESOURCE_TWO',
    },
    {
      id: 9,
      name: props.submenuNames[2],
      content: <ProjectResourceThreeTable {...props} />,
      key: 'RESOURCE_THREE',
    },
    {
      id: 10,
      name: 'Kapcsolattartó',
      content: <ProjectContact {...props} />,
      key: 'CONTACT',
    },
    {
      id: 11,
      name: 'Csatolmányok',
      content:
        params.USE_FILETAB_V2 === 'Y' ? (
          <FileTabV2
            disabled={fileTabDisabled}
            savedFiles={[
              ...(values?.projectAttachment ?? []),
              ...(props.selectedValues.taskLists ?? []).flatMap((taskList) => taskList.taskListAttachment ?? []),
              ...(params.PROJECT_SHOW_EMPLOYEEATTENDANCE_ATTACHMENTS === 'Y'
                ? props.selectedValues.employeeAttendanceAttachment ?? []
                : []),
            ]}
            savedFolders={addField && selectedValues.projectId ? savedProjectFolders : []}
            newFolders={values.projectFolders ?? []}
            fileFolderAssignments={values.fileFolderAssignments ?? []}
            handleUpload={handleUpload}
            handleCreateFolder={handleCreateFolder}
            handleDelete={(file) => {
              setFileToDelete(file);
              setDeletDialog(true);
            }}
            handleMoveFile={handleMoveFile}
            handleMoveFiles={handleMoveFiles}
            handleDeleteFiles={handleDeleteFiles}
            handleEditFolder={handleEditFolder}
            handleDeleteFolder={handleDeleteFolder}
            canMoveFile={canManageFile}
            canDeleteFile={canManageFile}
            canManageFolders={Boolean(props.canEdit)}
            handleImportFolder={handleImportProjectFolder}
            handleRenameFile={handleRenameFile}
            canRenameFile={canManageFile}
          />
        ) : (
          <FileTabUI
            datagridIdentifier={DataGridIdentifiers.uploadFileWithSource}
            disabled={disabled}
            savedFiles={[
              ...(values?.projectAttachment ?? []),
              ...(props.selectedValues.taskLists ?? []).flatMap((taskList) => taskList.taskListAttachment ?? []),
              ...(params.PROJECT_SHOW_EMPLOYEEATTENDANCE_ATTACHMENTS === 'Y'
                ? props.selectedValues.employeeAttendanceAttachment ?? []
                : []),
            ]}
            parentObj={FormType.PROJECT}
            handleUpload={handleUpload}
            handleDelete={(file) => {
              setFileToDelete(file);
              setDeletDialog(true);
            }}
          />
        ),
      key: 'ATTACHMENT',
    },
    {
      id: 12,
      name: 'Kommunikáció',
      content: <ProjectCommunication {...props} />,
      key: 'COMMUNICATION',
    },
    {
      id: 13,
      name: props.submenuNames[3],
      content: <ProjectResourceFourTable {...props} />,
      key: 'RESOURCE_FOUR',
    },
    {
      id: 14,
      name: props.submenuNames[4],
      content: <ProjectResourceFiveTable {...props} />,
      key: 'RESOURCE_FIVE',
    },
    {
      id: 15,
      name: 'Projekt történet',
      content: <ProjectHistory {...props} />,
      key: 'PROJECT_HISTORY',
    },
    {
      id: 16,
      name: 'Adat import',
      content: <ExcelImportTab {...props} />,
      key: 'PROJECT_IMPORT',
    },
    {
      id: 17,
      name: 'Szerződött árak',
      content: <ProjectPriceTable {...props} />,
      key: 'PROJECT_PRICE',
    },
    {
      id: 18,
      name: 'Fizetési ütemezés',
      content: <ProjectPaymentScheduleTable {...props} />,
      key: 'PAYMENT_SCHEDULE',
    },
    {
      id: 19,
      name: 'Alvállalkozók',
      content: <ProjectSubcontractorsTable {...props} />,
      key: 'SUBCONTRACTORS',
    },
    {
      id: 20,
      name: 'Dolgozói jelenlétek',
      content: <ProjectEmployeeAttendanceDetailsTable {...props} />,
      key: 'EMPLOYEE_ATTENDANCE_DETAILS',
    },
    {
      id: 21,
      name: 'Pótmunka',
      content: <ProjectExtraWorkSimpleTasksTable {...props} />,
      key: 'EXTRA_WORK_SIMPLETASKS',
    },
    {
      id: 22,
      name: 'Feladatok',
      content: <ProjectSimpleTasksTable {...props} />,
      key: 'PROJECT_SIMPLETASKS',
    },
    ...(params.PROJECT_JOKER_LINE_TAB_NAME
      ? [
          {
            id: 23,
            name: params.PROJECT_JOKER_LINE_TAB_NAME,
            content: <ProjectJokerLine {...props} />,
            key: 'PROJECT_JOKER_LINE',
          },
        ]
      : []),
    ...((jokerGroups.length > 0 &&
      jokerGroups
        .filter((group) => group.jokerFields.length > 0)
        .map((group, index) => ({
          id: index + 24,
          name: group.name,
          key: `JOKER_${group.id}`,
          content: <JokerTab fields={group.jokerFields} key={index} {...props} />,
        }))) ||
      []),
  ];

  const visibleTabs = useMemo(() => {
    const visibilityConfig = JSON.parse(params.PROJECT_TAB_FIELDS || '[]');
    const orderConfig = JSON.parse(params.PROJECT_TAB_ORDER || '[]');
    const disabledWithoutEditPermession = JSON.parse(params.PROJECT_DISABLE_TABS_WITHOUT_EDIT_PERMISSION || '[]');

    const tabVisibilityMap = Object.assign({}, ...(visibilityConfig || []));
    const tabOrderMap = Object.assign({}, ...(orderConfig || []));

    const filteredTabs = tabs.filter((tab) => {
      if (!props.canEdit && props.infoPanel && disabledWithoutEditPermession.includes(tab.key)) {
        return false;
      }
      if (!params.PROJECT_TAB_FIELDS) {
        return true;
      }
      if (tab.key.startsWith('RESOURCE_')) {
        const resourceIndex = parseInt(tab.key.split('_')[1]) - 1;
        return (
          (!tabVisibilityMap[tab.key] || tabVisibilityMap[tab.key] === 'Y') &&
          props.submenuNames[resourceIndex] !== null
        );
      }

      return !tabVisibilityMap[tab.key] || tabVisibilityMap[tab.key] === 'Y';
    });

    const ordered = [];
    const unordered = [];

    filteredTabs.forEach((tab) => {
      if (tabOrderMap[tab.key] !== undefined) {
        ordered.push({
          ...tab,
          visOrder: tabOrderMap[tab.key],
        });
      } else {
        unordered.push(tab);
      }
    });

    ordered.sort((a, b) => a.visOrder - b.visOrder);

    return [...ordered, ...unordered];
  }, [params.PROJECT_TAB_FIELDS, params.PROJECT_TAB_ORDER, tabs, props.submenuNames]);

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  useEffect(() => {
    const hasPropertiesTab = visibleTabs.some((tab) => tab.key === 'PROPERTIES');

    if (!hasPropertiesTab) {
      setDynLoading(false);
    }
  }, [visibleTabs]);

  return (
    <div className="my-4 mt-6">
      <div className="md:px-3">
        <TabsUI activeTab={activeTab} handleTabClick={handleTabClick}>
          {visibleTabs.map((tab, index) => (
            <div key={index} name={tab.name}>
              {tab.content}
            </div>
          ))}
        </TabsUI>
      </div>
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={deleteDialog}
        onHide={() => {
          hideDeleteDialog();
          setFileToDelete(null);
        }}
      />
    </div>
  );
};

export default ProjectTabs;
